import { createStaffColumns, StaffActionType } from "@/colDef/staffColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import Search from "@/components/Search";
import StaffDialog from "@/components/formDialog/StaffDialog";
import { ActionDropdown, ActionDropdownContent, ActionDropdownTrigger, DropdownNode } from "@/components/ActionDropdown";
import {
  useDeleteStaffMutation,
  useStaffQuery,
} from "@/hooks/api/staff.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import useUrlFilters from "@/hooks/useUrlFilters";
import {
  StaffParamsType,
  StaffType,
} from "@/services/staff.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, } from "react";
import { useNavigate } from "react-router-dom";

const StaffPage = () => {
  const navigate = useNavigate();

  const {
    page,
    limit,
    search,
    getFilter,
    setPage,
    setSearch,
    setFilter,
  } = useUrlFilters({ defaultLimit: 10 });

  const is_active_filter = getFilter("is_active");
  const designation_id = getFilter("designation_id");
  const designation_name = getFilter("designation_name");
  const debouncedSearch = useDebounce(search, 400);

  const deleteDialog = useAlertDialog();
  const staffDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    staffId: "",
  });

  // Auto-open if navigating here from StaffDialog (inline create)
  useEffect(() => {
    const draftStr = sessionStorage.getItem("inline_create_draft");
    if (draftStr) {
      try {
        const draft = JSON.parse(draftStr);
        if (draft.sourcePath && draft.sourcePath.startsWith("/people/staff") && draft.newValue) {
          staffDialog.open({ mode: draft.draftData.mode, staffId: draft.draftData.staffId });
        }
      } catch (e) {
        console.error("Failed to parse inline_create_draft", e);
      }
    }
  }, []);

  const filters: StaffParamsType = useMemo(
    () => ({
      limit,
      page,
      sortBy: "createdAt",
      sortOrder: "desc",
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(is_active_filter && { is_active: is_active_filter === "true" }),
      ...(designation_id && { designation_id }),
    }),
    [debouncedSearch, is_active_filter, page, limit, designation_id]
  );

  const { data, isLoading, isError, refetch } = useStaffQuery(filters);
  const deleteMutation = useDeleteStaffMutation(filters);

  const staffList = data?.staff ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: StaffActionType) => {
      if (payload.action === "view") {
        navigate(`/people/staff/${payload.id}`);
      } else if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to delete{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        staffDialog.open({ mode: "edit", staffId: payload.id });
      }
    },
    [deleteDialog, staffDialog, staffList, navigate]
  );

  const handleCreateClick = () => {
    staffDialog.open({ mode: "create", staffId: "" });
  };

  const columns = useMemo(
    () => createStaffColumns(handleActionClick),
    [handleActionClick]
  );

  const handleSetStatus = (val: string) => {
    setFilter("is_active", val === "all" ? "" : val);
  };

  const statusDropdownShape: DropdownNode[] = [
    { type: "item", label: "All", onSelect: () => handleSetStatus("all") },
    {
      type: "item",
      label: "Active",
      onSelect: () => handleSetStatus("true"),
    },
    {
      type: "item",
      label: "Inactive",
      onSelect: () => handleSetStatus("false"),
    },
  ];

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load staff members."
        onRetry={() => refetch()}
      />
    );
  }

  const triggerLabel =
    !is_active_filter || is_active_filter === ""
      ? "Status: All"
      : `Status: ${is_active_filter === "true" ? "Active" : "Inactive"}`;

  const formatDesignationName = (name: string) => {
    if (!name) return "Faculty";
    if (name.toLowerCase() === "hod") return "HOD";
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const displayTitle = designation_name ? formatDesignationName(designation_name) : "Faculty";

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{displayTitle} Management</h1>
        <Button
          onClick={handleCreateClick}
          className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
        >
          <Plus size={16} />
          Add New
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search..."
          value={search}
          onValueChange={setSearch}
          className={{ inputGroupClass: "max-w-xs" }}
        />

        <ActionDropdown menuModal={false}>
          <ActionDropdownTrigger
            triggerContent={
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-gray-500" />
                <span className="text-sm">{triggerLabel}</span>
              </div>
            }
            buttonVariant="outline"
            buttonSize="default"
            className={{
              buttonClass: "h-9 bg-white font-normal text-gray-700",
            }}
          />
          <ActionDropdownContent
            align="start"
            dropdownShape={statusDropdownShape}
            className="w-40"
          />
        </ActionDropdown>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<StaffType, any>
              columns={columns}
              data={staffList}
              // onRowClick={(member) => navigate(`/people/staff/${member.id}`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={staffList.length}
        onPageChange={setPage}
      />

      {/* Staff Create / Edit Dialog */}
      <StaffDialog
        mode={staffDialog.state.mode}
        isOpen={staffDialog.state.isOpen}
        onOpenChange={(open) => !open && staffDialog.close()}
        filters={filters}
        staffId={staffDialog.state.staffId}
        onCancel={staffDialog.close}
        prefillDesignation={
          designation_id && designation_name
            ? { id: String(designation_id), name: String(designation_name) }
            : undefined
        }
      />

      {/* Delete Confirmation Dialog */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default StaffPage;
